/**
 * Service for interacting with Ory Kratos (if enabled)
 */

const KRATOS_URL = import.meta.env.VITE_KRATOS_URL || 'http://localhost:4433';

export interface KratosMessage {
  id: number;
  text: string;
  type: string;
}

export interface KratosNode {
  type: string;
  group: string;
  attributes: {
    name: string;
    type: string;
    value?: string | boolean;
    required?: boolean;
    disabled?: boolean;
    /** img nodes (the TOTP QR code). */
    src?: string;
    id?: string;
    /** text nodes (the TOTP secret, backup codes). */
    text?: {
      text: string;
      context?: { secrets?: Array<{ text: string; context?: { used_at?: string } }> };
    };
    /** a nodes (a passed verification flow's "continue" link). */
    href?: string;
  };
  /** Social sign-in labels carry the provider's display name in the context. */
  meta?: { label?: { text: string; context?: { provider?: string; provider_id?: string } } };
  messages?: KratosMessage[];
}

/**
 * What Kratos asks the UI to do next (`continue_with`): e.g. show the
 * verification flow it just started and mailed a code for.
 */
export interface KratosContinueWith {
  action: string;
  flow?: { id: string; verifiable_address?: string; url?: string };
  redirect_browser_to?: string;
}

/**
 * A Kratos self-service flow (login, recovery, verification, ...). Covers
 * both the initial `/browser` GET response and the (looser) shape a
 * submission POST can return — a still-open flow with `ui`, or a completed
 * one carrying `redirect_browser_to`.
 */
export interface KratosFlow {
  id: string;
  state?: string;
  ui: {
    action: string;
    method: string;
    nodes: KratosNode[];
    messages?: KratosMessage[];
  };
  redirect_browser_to?: string;
  expires_at?: string;
  /** True when this flow re-confirms an existing session (step-up). */
  refresh?: boolean;
  /** Login flows: aal2 when Kratos wants the second factor. */
  requested_aal?: string;
  /**
   * Where Kratos sends the browser when the flow completes. The sign-in pages
   * set it to themselves with the OAuth2 login_challenge, so a flow Kratos
   * drives (social sign-in, account linking) still knows the application.
   */
  return_to?: string;
  /** Registration submissions: e.g. show_verification_ui with its flow id. */
  continue_with?: KratosContinueWith[];
}

// Kept as an alias — call sites predating the flow consolidation referred to
// this name explicitly.
export type KratosLoginFlow = KratosFlow;

export interface KratosLogoutFlow {
  logout_url: string;
  logout_token: string;
}

export interface KratosErrorPayload {
  id?: string;
  error?: {
    id?: string;
    code?: number;
    status?: string;
    reason?: string;
    message?: string;
    /** e.g. session_verified_address_required: the verification flow Kratos started. */
    details?: { continue_with?: KratosContinueWith[] };
  };
  ui?: {
    messages?: KratosMessage[];
  };
  [key: string]: unknown;
}

/** The verification flow Kratos started and mailed a code for, if it says so. */
export function verificationFlowOf(continueWith: KratosContinueWith[] | undefined): string | undefined {
  return continueWith?.find((item) => item.action === 'show_verification_ui')?.flow?.id;
}

/** A named input node's value, e.g. the address a verification flow mailed. */
export function nodeValue(flow: KratosFlow | null, name: string): string {
  const value = flow?.ui?.nodes?.find((node) => node.attributes?.name === name)?.attributes.value;
  return typeof value === 'string' ? value : '';
}

/**
 * Error raised for a failed self-service flow submission. Wraps the parsed
 * Kratos error/flow payload (when available) so callers can branch on
 * structured fields — e.g. `err.kratosError?.error?.id ===
 * 'session_aal2_required'` — instead of parsing the message string.
 */
export class KratosFlowError extends Error {
  kratosError?: KratosErrorPayload;

  constructor(message: string, kratosError?: KratosErrorPayload) {
    super(message);
    this.name = 'KratosFlowError';
    this.kratosError = kratosError;
  }
}

// SPA-native Kratos calls: Accept: application/json makes the /browser flow
// endpoints return the flow document directly (200) instead of a 303 redirect
// to the ui_url, which a fetch() would follow into the SPA's own HTML.
const JSON_ACCEPT = { Accept: 'application/json' };

async function parseJsonSafe(response: Response): Promise<KratosErrorPayload | undefined> {
  try {
    return await response.json();
  } catch {
    return undefined;
  }
}

function messageFromPayload(payload: KratosErrorPayload | undefined, fallback: string): string {
  const uiMessage = payload?.ui?.messages?.[0]?.text;
  // Field-level validation errors (e.g. a trait the identity schema rejects)
  // live on the individual nodes, not on ui.messages — without this they are
  // swallowed and every failure looks like a generic one.
  const ui = payload?.ui as { nodes?: KratosNode[] } | undefined;
  const nodeMessage = ui?.nodes
    ?.flatMap((node) => node.messages ?? [])
    .find((message) => message.type === 'error')?.text;
  const errorMessage = payload?.error?.message || payload?.error?.reason;
  return uiMessage || nodeMessage || errorMessage || fallback;
}

/** Shared error extraction for callers that fetch Kratos flows directly. */
export function flowErrorMessage(payload: unknown, fallback: string): string {
  return messageFromPayload(payload as KratosErrorPayload | undefined, fallback);
}

export interface KratosSession {
  identity?: { traits?: Record<string, unknown> };
}

export interface KratosSessionState {
  session: KratosSession | null;
  /**
   * Signed in with the password, but the account has a second factor that
   * hasn't been given yet: Kratos refuses the session (403
   * session_aal2_required) until it is. Send the visitor to /mfa.
   */
  secondFactorRequired: boolean;
}

/** Where to send a visitor who owes their second factor, coming back to returnTo. */
export function mfaPath(returnTo: string): string {
  return `/mfa?return_to=${encodeURIComponent(returnTo)}`;
}

export class KratosService {
  /**
   * Return the active Kratos session, or null when not signed in (or Kratos
   * is unreachable). The cookie is the credential; no token storage involved.
   */
  async whoami(): Promise<KratosSession | null> {
    return (await this.sessionState()).session;
  }

  /** Like whoami, but tells "signed out" apart from "second factor owed". */
  async sessionState(): Promise<KratosSessionState> {
    try {
      const response = await fetch(`${KRATOS_URL}/sessions/whoami`, {
        credentials: 'include',
        headers: JSON_ACCEPT,
      });
      if (response.status === 403) {
        const payload = await parseJsonSafe(response);
        return {
          session: null,
          secondFactorRequired: payload?.error?.id === 'session_aal2_required',
        };
      }
      if (!response.ok) return { session: null, secondFactorRequired: false };
      return { session: await response.json(), secondFactorRequired: false };
    } catch {
      return { session: null, secondFactorRequired: false };
    }
  }

  /**
   * Submit a settings-flow method (profile, password, totp, lookup_secret)
   * and return the updated flow Kratos answers with. Failures raise
   * KratosFlowError so callers can branch on session_refresh_required /
   * session_aal2_required.
   */
  async submitSettings(flowId: string, body: Record<string, unknown>): Promise<KratosFlow> {
    const csrf_token = await this.getCsrfToken('settings', flowId);
    const response = await fetch(`${KRATOS_URL}/self-service/settings?flow=${encodeURIComponent(flowId)}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...JSON_ACCEPT },
      credentials: 'include',
      body: JSON.stringify({ csrf_token, ...body }),
    });
    const payload = await parseJsonSafe(response);
    if (!response.ok) {
      throw new KratosFlowError(messageFromPayload(payload, 'Saving your settings failed'), payload);
    }
    return payload as unknown as KratosFlow;
  }

  /**
   * Browser (cookie-based) flows are CSRF-protected: every submission must
   * echo the `csrf_token` that Kratos put in the flow's ui.nodes. Callers
   * only hold the flow id, so re-read the flow and pull the token out.
   * (Public: Settings drives its own settings-flow fetches.)
   */
  async getCsrfToken(
    flowType: 'login' | 'recovery' | 'verification' | 'settings' | 'registration',
    flowId: string
  ): Promise<string | undefined> {
    try {
      const response = await fetch(
        `${KRATOS_URL}/self-service/${flowType}/flows?id=${encodeURIComponent(flowId)}`,
        { credentials: 'include', headers: JSON_ACCEPT }
      );
      if (!response.ok) return undefined;
      const flow = (await response.json()) as KratosFlow;
      const node = flow.ui?.nodes?.find((n) => n.attributes?.name === 'csrf_token');
      const value = node?.attributes?.value;
      return typeof value === 'string' ? value : undefined;
    } catch {
      return undefined;
    }
  }

  /**
   * Start a browser flow (`/self-service/<type>/browser`). Non-OK answers
   * are structured so callers can branch on e.g. session_already_available
   * (a signed-in visitor hitting /login gets a 400, not an outage).
   */
  private async initBrowserFlow(
    flowType: 'login' | 'registration',
    params: URLSearchParams
  ): Promise<KratosFlow> {
    const qs = params.toString();
    const response = await fetch(`${KRATOS_URL}/self-service/${flowType}/browser${qs ? `?${qs}` : ''}`, {
      credentials: 'include',
      headers: JSON_ACCEPT,
    });
    if (!response.ok) {
      const payload = await parseJsonSafe(response);
      // A return_to Kratos won't honour (selfservice.allowed_return_urls)
      // only costs the way back to a pending sign-in; the flow works without.
      if (params.has('return_to') && payload?.error?.id === 'self_service_flow_return_to_forbidden') {
        params.delete('return_to');
        return this.initBrowserFlow(flowType, params);
      }
      throw new KratosFlowError(
        messageFromPayload(payload, `Failed to initialize ${flowType} flow: ${response.statusText}`),
        payload
      );
    }
    return await response.json();
  }

  /**
   * Initialize a login flow with Kratos.
   * Pass `aal: 'aal2'` to request a second-factor (MFA) challenge, and
   * `returnTo` for where Kratos sends the browser when it finishes the flow
   * itself (social sign-in, the emailed verification link).
   */
  async initLoginFlow(aal?: 'aal1' | 'aal2', refresh?: boolean, returnTo?: string): Promise<KratosFlow> {
    try {
      const params = new URLSearchParams();
      if (aal) params.set('aal', aal);
      if (refresh) params.set('refresh', 'true');
      if (returnTo) params.set('return_to', returnTo);
      return await this.initBrowserFlow('login', params);
    } catch (error) {
      console.error('Error initializing Kratos login flow:', error);
      throw error;
    }
  }

  /**
   * Fetch an existing flow by id — one Kratos sent the browser back with as
   * `?flow=` (a social sign-in that needs more, an emailed verification
   * link, a linked account). Adopting it keeps the state it carries.
   */
  async getFlow(
    flowType: 'registration' | 'verification' | 'settings',
    flowId: string
  ): Promise<KratosFlow> {
    const response = await fetch(
      `${KRATOS_URL}/self-service/${flowType}/flows?id=${encodeURIComponent(flowId)}`,
      { credentials: 'include', headers: JSON_ACCEPT }
    );
    if (!response.ok) {
      const payload = await parseJsonSafe(response);
      throw new KratosFlowError(
        messageFromPayload(payload, `Failed to load ${flowType} flow: ${response.statusText}`),
        payload
      );
    }
    return await response.json();
  }

  /**
   * Start a social sign-in or sign-up: submit the oidc method (`provider`,
   * plus `traits` when finishing a sign-up the provider left incomplete) and
   * return the provider URL Kratos wants the browser at — to a JSON caller it
   * answers 422 browser_location_change_required rather than redirecting.
   */
  async startSocial(
    flowType: 'login' | 'registration',
    flowId: string,
    body: { provider: string; traits?: Record<string, unknown> }
  ): Promise<string> {
    const csrf_token = await this.getCsrfToken(flowType, flowId);
    const response = await fetch(`${KRATOS_URL}/self-service/${flowType}?flow=${encodeURIComponent(flowId)}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...JSON_ACCEPT },
      credentials: 'include',
      body: JSON.stringify({ csrf_token, method: 'oidc', ...body }),
    });
    const payload = await parseJsonSafe(response);
    const target = payload?.redirect_browser_to;
    if (typeof target === 'string' && target) return target;
    throw new KratosFlowError(messageFromPayload(payload, 'Could not start signing in with that provider'), payload);
  }

  /**
   * Submit login credentials (password method) to Kratos.
   */
  async submitLogin(flowId: string, identifier: string, password: string): Promise<KratosFlow> {
    try {
      const csrf_token = await this.getCsrfToken('login', flowId);
      const response = await fetch(`${KRATOS_URL}/self-service/login?flow=${flowId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...JSON_ACCEPT },
        credentials: 'include',
        body: JSON.stringify({
          method: 'password',
          identifier,
          password,
          csrf_token,
        }),
      });

      const payload = await parseJsonSafe(response);
      if (!response.ok) {
        throw new KratosFlowError(messageFromPayload(payload, 'Login failed'), payload);
      }

      return payload as unknown as KratosFlow;
    } catch (error) {
      console.error('Error submitting login:', error);
      throw error;
    }
  }

  /**
   * Submit an arbitrary login method payload against an existing login flow
   * — used for second-factor methods (TOTP, lookup/backup codes) that
   * `submitLogin`'s narrower password signature doesn't cover.
   */
  async submitLoginMethod(flowId: string, body: Record<string, unknown>): Promise<KratosFlow> {
    try {
      const csrf_token = await this.getCsrfToken('login', flowId);
      const response = await fetch(`${KRATOS_URL}/self-service/login?flow=${flowId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...JSON_ACCEPT },
        credentials: 'include',
        body: JSON.stringify({ csrf_token, ...body }),
      });

      const payload = await parseJsonSafe(response);
      if (!response.ok) {
        throw new KratosFlowError(messageFromPayload(payload, 'Verification failed'), payload);
      }

      return payload as unknown as KratosFlow;
    } catch (error) {
      console.error('Error submitting login method:', error);
      throw error;
    }
  }

  /**
   * Fetch an EXISTING login flow by id. Kratos redirects back to the sign-in
   * UI with `?flow=` for refresh/step-up and OAuth2 login challenges; adopting
   * that flow is required — starting a fresh one loses the context.
   */
  async getLoginFlow(flowId: string): Promise<KratosFlow> {
    const response = await fetch(
      `${KRATOS_URL}/self-service/login/flows?id=${encodeURIComponent(flowId)}`,
      { credentials: 'include', headers: JSON_ACCEPT }
    );
    if (!response.ok) {
      const payload = await parseJsonSafe(response);
      throw new KratosFlowError(
        messageFromPayload(payload, `Failed to load login flow: ${response.statusText}`),
        payload
      );
    }
    return await response.json();
  }

  /**
   * Initialize a registration flow. Throws KratosFlowError (with payload) on
   * a non-OK response — e.g. session_already_available for signed-in users.
   * `returnTo` is where the verification that follows sign-up continues to.
   */
  async initRegistrationFlow(returnTo?: string): Promise<KratosFlow> {
    const params = new URLSearchParams();
    if (returnTo) params.set('return_to', returnTo);
    return this.initBrowserFlow('registration', params);
  }

  /**
   * Submit the registration form (password method) — echoes the flow's
   * csrf_token like every other browser-flow submission.
   */
  async submitRegistration(flowId: string, body: Record<string, unknown>): Promise<KratosFlow> {
    const csrf_token = await this.getCsrfToken('registration', flowId);
    const response = await fetch(`${KRATOS_URL}/self-service/registration?flow=${flowId}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...JSON_ACCEPT },
      credentials: 'include',
      body: JSON.stringify({ csrf_token, ...body }),
    });
    const payload = await parseJsonSafe(response);
    if (!response.ok) {
      throw new KratosFlowError(messageFromPayload(payload, 'Registration failed'), payload);
    }
    return payload as unknown as KratosFlow;
  }

  /**
   * Initialize a recovery ("forgot password") flow.
   */
  async initRecoveryFlow(): Promise<KratosFlow> {
    try {
      const response = await fetch(`${KRATOS_URL}/self-service/recovery/browser`, {
        credentials: 'include',
        headers: JSON_ACCEPT,
      });
      if (!response.ok) {
        throw new Error(`Failed to initialize recovery flow: ${response.statusText}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Error initializing Kratos recovery flow:', error);
      throw error;
    }
  }

  /**
   * Submit a step of the recovery flow (first the email, then the code).
   */
  async submitRecovery(flowId: string, body: Record<string, unknown>): Promise<KratosFlow> {
    try {
      const csrf_token = await this.getCsrfToken('recovery', flowId);
      const response = await fetch(`${KRATOS_URL}/self-service/recovery?flow=${flowId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...JSON_ACCEPT },
        credentials: 'include',
        body: JSON.stringify({ csrf_token, ...body }),
      });

      const payload = await parseJsonSafe(response);
      if (!response.ok) {
        throw new KratosFlowError(messageFromPayload(payload, 'Recovery failed'), payload);
      }
      return payload as unknown as KratosFlow;
    } catch (error) {
      console.error('Error submitting recovery:', error);
      throw error;
    }
  }

  /**
   * Initialize an email verification flow.
   */
  async initVerificationFlow(): Promise<KratosFlow> {
    try {
      const response = await fetch(`${KRATOS_URL}/self-service/verification/browser`, {
        credentials: 'include',
        headers: JSON_ACCEPT,
      });
      if (!response.ok) {
        throw new Error(`Failed to initialize verification flow: ${response.statusText}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Error initializing Kratos verification flow:', error);
      throw error;
    }
  }

  /**
   * Submit a step of the verification flow (first the email, then the code).
   */
  async submitVerification(flowId: string, body: Record<string, unknown>): Promise<KratosFlow> {
    try {
      const csrf_token = await this.getCsrfToken('verification', flowId);
      const response = await fetch(`${KRATOS_URL}/self-service/verification?flow=${flowId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...JSON_ACCEPT },
        credentials: 'include',
        body: JSON.stringify({ csrf_token, ...body }),
      });

      const payload = await parseJsonSafe(response);
      if (!response.ok) {
        throw new KratosFlowError(messageFromPayload(payload, 'Verification failed'), payload);
      }
      return payload as unknown as KratosFlow;
    } catch (error) {
      console.error('Error submitting verification:', error);
      throw error;
    }
  }

  /**
   * Initialize the browser logout flow: returns a one-time logout URL/token.
   */
  async initLogoutFlow(): Promise<KratosLogoutFlow> {
    try {
      const response = await fetch(`${KRATOS_URL}/self-service/logout/browser`, {
        credentials: 'include',
        headers: JSON_ACCEPT,
      });
      if (!response.ok) {
        throw new Error(`Failed to initialize logout flow: ${response.statusText}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Error initializing Kratos logout flow:', error);
      throw error;
    }
  }

  /**
   * Complete logout by consuming the logout URL from `initLogoutFlow`.
   *
   * This is a browser NAVIGATION, not a fetch: Kratos answers with a redirect
   * to the configured post-logout URL and clears the session cookie on the
   * way. Fetching it cross-origin fails CORS and leaves the session intact.
   */
  submitLogout(logoutUrl: string): void {
    window.location.href = logoutUrl;
  }

  /**
   * Fetch the payload for a self-service error id (Kratos redirects to
   * `/error?id=` for flow errors it can't render inline).
   */
  async getFlowError(errorId: string): Promise<KratosErrorPayload> {
    try {
      const response = await fetch(`${KRATOS_URL}/self-service/errors?id=${encodeURIComponent(errorId)}`, {
        credentials: 'include',
        headers: JSON_ACCEPT,
      });
      if (!response.ok) {
        throw new Error(`Failed to fetch flow error: ${response.statusText}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Error fetching Kratos flow error:', error);
      throw error;
    }
  }

  /**
   * Check if Kratos is available
   */
  async isAvailable(): Promise<boolean> {
    try {
      const response = await fetch(`${KRATOS_URL}/health/ready`, {
        method: 'GET',
      });
      return response.ok;
    } catch (error) {
      return false;
    }
  }
}

export const kratosService = new KratosService();
